import { useMemo } from 'react';
import { useQuery } from '@apollo/client';

import { useCurrentUserContext } from '.';
import currentUserManagedPlatformAccountsQuery from './currentUserManagedPlatformAccounts.graphql';

type SegmentCapabilityKey = keyof Omit<
  FleetAdminSegmentPermissions,
  '__typename'
>;
type AccountCapabilityKey = keyof Omit<
  FleetAdminAccountPermissions,
  '__typename'
>;

export type ResolvedFleetAdminCapability = FleetAdminCapability & {
  loading?: boolean;
};

interface UseFleetAdminPermissionsResult {
  forSegment(oemSegmentId: string): FleetAdminSegmentPermissions | undefined;

  capability(
    oemSegmentId: string | null | undefined,
    key: SegmentCapabilityKey,
  ): FleetAdminCapability;

  anySegment(key: SegmentCapabilityKey): FleetAdminCapability;

  forAccount(
    platformAccountId: string,
  ): FleetAdminAccountPermissions | undefined;

  accountCapability(
    platformAccountId: string | null | undefined,
    key: AccountCapabilityKey,
  ): ResolvedFleetAdminCapability;

  anyAccount(key: AccountCapabilityKey): ResolvedFleetAdminCapability;
}

const SYNTHETIC_DENIED: FleetAdminCapability = {
  __typename: 'FleetAdminCapability',
  allowed: false,
  deniedReason: undefined,
};

const SYNTHETIC_LOADING: ResolvedFleetAdminCapability = {
  __typename: 'FleetAdminCapability',
  allowed: false,
  deniedReason: undefined,
  loading: true,
};

export default function useFleetAdminPermissions(): UseFleetAdminPermissionsResult {
  const { oemSegments, managedPlatformAccounts: contextAccounts } =
    useCurrentUserContext();

  const { data: managedAccountsData, loading: accountsLoading } =
    useQuery<CurrentUserManagedPlatformAccountsQuery>(
      currentUserManagedPlatformAccountsQuery,
      { fetchPolicy: 'cache-first', skip: contextAccounts !== undefined },
    );
  const managedPlatformAccounts =
    contextAccounts ??
    managedAccountsData?.currentUserProfile?.user?.managedPlatformAccounts;
  const accountsPending =
    managedPlatformAccounts === undefined && accountsLoading;

  return useMemo(() => {
    const segments = oemSegments ?? [];
    const segmentById = new Map(
      segments.map((segment) => [segment.id, segment]),
    );

    const accounts = managedPlatformAccounts ?? [];
    const accountById = new Map(
      accounts.map((account) => [account.id, account]),
    );

    return {
      forSegment(id) {
        return segmentById.get(id)?.fleetAdminPermissions;
      },

      capability(id, key) {
        if (id == null) return SYNTHETIC_DENIED;
        const perms = segmentById.get(id)?.fleetAdminPermissions;
        return perms?.[key] ?? SYNTHETIC_DENIED;
      },

      anySegment(key) {
        if (segments.length === 0) return SYNTHETIC_DENIED;
        const allowed = segments.find(
          (segment) => segment.fleetAdminPermissions[key].allowed,
        );
        if (allowed) return allowed.fleetAdminPermissions[key];

        return segments[0].fleetAdminPermissions[key];
      },

      forAccount(id) {
        return accountById.get(id)?.fleetAdminPermissions;
      },

      accountCapability(id, key) {
        if (accountsPending) return SYNTHETIC_LOADING;
        if (id == null) return SYNTHETIC_DENIED;
        const perms = accountById.get(id)?.fleetAdminPermissions;
        return perms?.[key] ?? SYNTHETIC_DENIED;
      },

      anyAccount(key) {
        if (accountsPending) return SYNTHETIC_LOADING;
        if (accounts.length === 0) return SYNTHETIC_DENIED;
        const allowed = accounts.find(
          (account) => account.fleetAdminPermissions[key].allowed,
        );
        if (allowed) return allowed.fleetAdminPermissions[key];

        return accounts[0].fleetAdminPermissions[key];
      },
    };
  }, [oemSegments, managedPlatformAccounts, accountsPending]);
}
